import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { getModuleKeyForPath } from '@/constants/adminModules';
import { ROUTES } from '@/constants/routes';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useNotify } from '@/hooks/useNotify';
import { useGetUserDetailQuery } from '@/modules/user/userApi';
import { useAppSelector } from '@/store/hooks';
import { getAccessibleFallbackRoute, hasModuleAccess } from '@/utils/permissions';

export { DocumentTitleProvider } from './DocumentTitleProvider';

export function GuestRoute() {
  const isAuthenticated = useIsAuthenticated();
  if (isAuthenticated) return <Navigate to={ROUTES.DASHBOARD} replace />;
  return <Outlet />;
}

export function ProtectedRoute() {
  const isAuthenticated = useIsAuthenticated();
  if (!isAuthenticated) return <Navigate to={ROUTES.LOGIN} replace />;
  return <Outlet />;
}

export function ModuleAccessRoute() {
  const location = useLocation();
  const notify = useNotify();
  const authUser = useAppSelector((store) => store.auth.user);
  const { data: userDetail, isLoading } = useGetUserDetailQuery();
  const user = userDetail ?? authUser;
  const lastDeniedPath = useRef<string | null>(null);

  const moduleKey = getModuleKeyForPath(location.pathname);
  const allowed = !moduleKey || hasModuleAccess(user, moduleKey);

  useEffect(() => {
    if (isLoading || allowed) return;
    if (lastDeniedPath.current === location.pathname) return;

    lastDeniedPath.current = location.pathname;
    notify.error('You do not have access to this page.');
  }, [allowed, isLoading, location.pathname, notify]);

  useEffect(() => {
    if (allowed) {
      lastDeniedPath.current = null;
    }
  }, [allowed, location.pathname]);

  if (isLoading && !user?.effectivePermissions?.length && user?.role !== 'super_admin') {
    return null;
  }

  if (!allowed) {
    const fallback = getAccessibleFallbackRoute(user);
    if (fallback === location.pathname) {
      return <Navigate to={ROUTES.PROFILE} replace />;
    }
    return <Navigate to={fallback} replace />;
  }

  return <Outlet />;
}

export function RootRedirect() {
  const isAuthenticated = useIsAuthenticated();
  return <Navigate to={isAuthenticated ? ROUTES.DASHBOARD : ROUTES.LOGIN} replace />;
}
